import {
  IList,
  isNotUndefined,
  useDataStream,
  useStateAsStream,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { filter, map } from 'rxjs/operators';
import { FetchErrorType } from '../../api/types/common/fetch-error-type';
import { FetchResultType } from '../../api/types/common/fetch-result';
import { IAPIValidationErrorType } from '../../api/types/common/validation-error';
import { LoginAPIRequestBodyType } from '../../api/types/login-response/api-request-body';
import { login, useLoggedIn } from '../../global-state/auth-state';
import { routes } from '../../routing/routes';
import { ILoginResponseType } from '../../types/record/login-response';
import { LoginMainView } from './login-main-view';

export const Login: FC<{}> = memo(() => {
  const loggedIn = useLoggedIn();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [result$, setResult] = useStateAsStream<
    | undefined
    | FetchResultType<
        ILoginResponseType,
        FetchErrorType<IAPIValidationErrorType<LoginAPIRequestBodyType>>
      >
  >(undefined);

  const emailErrors$ = useDataStream<IList<string>>(
    IList<string>(),
    result$.pipe(
      filter(isNotUndefined),
      map(res => {
        if (
          res.type === 'error' &&
          res.body.type === 'validation-error' &&
          res.body.content.email !== undefined
        ) {
          return res.body.content.email;
        } else {
          return IList<string>();
        }
      })
    )
  );

  const passwordErrors$ = useDataStream<IList<string>>(
    IList<string>(),
    result$.pipe(
      filter(isNotUndefined),
      map(res => {
        if (
          res.type === 'error' &&
          res.body.type === 'validation-error' &&
          res.body.content.password !== undefined
        ) {
          return res.body.content.password;
        } else {
          return IList<string>();
        }
      })
    )
  );

  const nonFieldErrors$ = useDataStream<IList<string>>(
    IList<string>(),
    result$.pipe(
      filter(isNotUndefined),
      map(res => {
        if (res.type === 'error' && res.body.type === 'non-field-errors') {
          return res.body.content;
        } else {
          return IList<string>();
        }
      })
    )
  );

  const detailErrors$ = useDataStream<string>(
    '',
    result$.pipe(
      filter(isNotUndefined),
      map(res => {
        if (res.type === 'error' && res.body.type === 'detail') {
          return res.body.content;
        } else {
          return '';
        }
      })
    )
  );

  const loginButtonIsClickable = useMemo(
    () => email !== '' && password !== '',
    [email, password]
  );

  const loginOnClick = useCallback(() => {
    if (loginButtonIsClickable) {
      login({ password, email }).then(setResult);
    }
  }, [loginButtonIsClickable, password, email, setResult]);

  /* extract values */

  const emailErrors = useStreamValue(emailErrors$, IList<string>());
  const passwordErrors = useStreamValue(passwordErrors$, IList<string>());
  const nonFieldErrors = useStreamValue(nonFieldErrors$, IList<string>());
  const detailErrors = useStreamValue<string>(detailErrors$, '');

  switch (loggedIn) {
    case undefined:
      return <div />;
    case true:
      return <Redirect to={routes.main} />;
    case false:
      return (
        <LoginMainView
          email={email}
          setEmail={setEmail}
          emailErrors={emailErrors}
          password={password}
          setPassword={setPassword}
          passwordErrors={passwordErrors}
          nonFieldErrors={nonFieldErrors}
          detailErrors={detailErrors}
          loginButtonIsClickable={loginButtonIsClickable}
          loginOnClick={loginOnClick}
        />
      );
    default:
      return <div />;
  }
});

Login.displayName = 'Login';
